/**
 * Feature Limit Registry
 * 
 * Provides a decoupled way for apps to register their limit checkers
 * and for permission gates to query limits without knowing implementation details.
 * 
 * Usage in app:
 * ```typescript
 * // In app initialization
 * registerLimitChecker('services', () => useServicesStore.getState().hasReachedLimit());
 * registerLimitChecker('teams', () => useTeamsStore.getState().hasReachedLimit());
 * ```
 * 
 * Usage in permission gate:
 * ```typescript
 * const hasReachedLimit = checkFeatureLimit('services');
 * if (hasReachedLimit) {
 *   // Show upgrade prompt
 * }
 * ```
 */

type LimitChecker = () => boolean;
type LimitInfo = { current: number; limit: number | null; hasReachedLimit: boolean };
type LimitInfoGetter = () => LimitInfo;
type DataChangeSubscriber = (callback: () => void) => () => void; // Returns unsubscribe function

const limitCheckers = new Map<string, LimitChecker>();
const limitInfoGetters = new Map<string, LimitInfoGetter>();
const dataChangeSubscribers = new Map<string, DataChangeSubscriber>();

/**
 * Register a limit checker for a feature
 * @param featureName - Name of the feature (e.g., 'services', 'teams', 'users')
 * @param checker - Function that returns true if limit is reached
 */
export function registerLimitChecker(featureName: string, checker: LimitChecker): void {
  limitCheckers.set(featureName, checker);
}

/**
 * Register a limit info getter for a feature
 * @param featureName - Name of the feature (e.g., 'services', 'teams', 'users')
 * @param getter - Function that returns detailed limit information
 */
export function registerLimitInfoGetter(featureName: string, getter: LimitInfoGetter): void {
  limitInfoGetters.set(featureName, getter);
}

/**
 * Register a data change subscriber for a feature
 * @param featureName - Name of the feature (e.g., 'services', 'teams', 'users')
 * @param subscriber - Function that subscribes to data changes and returns unsubscribe function
 */
export function registerDataChangeSubscriber(featureName: string, subscriber: DataChangeSubscriber): void {
  dataChangeSubscribers.set(featureName, subscriber);
}

/**
 * Check if a feature has reached its limit
 * @param featureName - Name of the feature to check
 * @returns true if limit is reached, false if not reached or no checker registered
 */
export function checkFeatureLimit(featureName: string): boolean {
  const checker = limitCheckers.get(featureName);
  if (!checker) {
    console.warn(`[LimitRegistry] No limit checker registered for feature: ${featureName}`);
    return false; // Fail open - don't block if no checker registered
  }
  return checker();
}

/**
 * Get detailed limit information for a feature
 * @param featureName - Name of the feature to check
 * @returns Limit info or null if no getter registered
 */
export function getFeatureLimitInfo(featureName: string): LimitInfo | null {
  const getter = limitInfoGetters.get(featureName);
  if (!getter) {
    console.warn(`[LimitRegistry] No limit info getter registered for feature: ${featureName}`);
    return null;
  }
  return getter();
}

/**
 * Subscribe to data changes for a feature
 * @param featureName - Name of the feature to subscribe to
 * @param callback - Function to call when data changes
 * @returns Unsubscribe function, or undefined if no subscriber registered
 */
export function subscribeToDataChanges(featureName: string, callback: () => void): (() => void) | undefined {
  const subscriber = dataChangeSubscribers.get(featureName);
  if (!subscriber) {
    console.warn(`[LimitRegistry] No data change subscriber registered for feature: ${featureName}`);
    return undefined;
  }
  return subscriber(callback);
}

/**
 * Unregister a limit checker (useful for cleanup in tests)
 */
export function unregisterLimitChecker(featureName: string): void {
  limitCheckers.delete(featureName);
  limitInfoGetters.delete(featureName);
  dataChangeSubscribers.delete(featureName);
}

/**
 * Clear all registered limit checkers (useful for cleanup in tests)
 */
export function clearAllLimitCheckers(): void {
  limitCheckers.clear();
  limitInfoGetters.clear();
  dataChangeSubscribers.clear();
}

/**
 * Get all registered feature names
 */
export function getRegisteredFeatures(): string[] {
  return Array.from(limitCheckers.keys());
}
